import React, { useState } from "react";
import { api } from "../lib/api";
import { Check, ArrowRight, DollarSign, Sparkles, Users } from "lucide-react";
import { toast } from "sonner";
import Logo from "../components/Logo";

// Value pillars — the "why join CORE Realty and Associates" pitch.
// Moved here from /tiers so the recruitment page owns the benefits story
// and /tiers is the exclusive home for fees + commission splits.
const PILLARS = [
  {
    id: "keep-more",
    icon: DollarSign,
    title: "Keep More of What You Earn",
    points: [
      "Flat transaction fee — no percentage skim",
      "$0 royalty · $0 brand tax",
      "Pay-as-you-close — no monthly cap chase",
      "Estimated agent retention: 92–97% per close",
    ],
  },
  {
    id: "boutique",
    icon: Sparkles,
    title: "Freedom + Boutique Support",
    points: [
      "Broker on call — same week, same broker",
      "Personalized listing reviews + contract support",
      "Marketing kit + GAR forms included",
    ],
  },
  {
    id: "client-first",
    icon: Users,
    title: "Client & Agent First",
    points: [
      "Tools and supervision built for ethics + outcomes",
      "Compliance reviews on every contract",
      "Mentorship for new licensees, no extra fee",
      "Professional agent expense ledger for automated tax and accounting support",
    ],
  },
];

export default function Recruit() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", years: "", message: "" });
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/public/apply", form);
      setSent(true);
      toast.success("Application received");
    } catch { toast.error("Failed to send"); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="recruit-page">
      <header className="border-b border-[#d1cece] px-8 py-5 flex items-center justify-between">
        <Logo variant="black" height={44} />
        <a href="/login" className="text-sm hover:text-[#bf858e]" data-testid="recruit-agent-login">Agent Login →</a>
      </header>

      <section className="relative px-8 lg:px-16 py-24 lg:py-32 overflow-hidden bg-[#050303]" data-testid="recruit-hero">
        {/* Aerial video background — muted, looping, autoplay. Poster falls back to the Atlanta skyline still. */}
        <video
          className="absolute inset-0 w-full h-full object-cover opacity-70"
          autoPlay muted loop playsInline preload="metadata"
          poster="https://images.pexels.com/photos/33133737/pexels-photo-33133737.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop"
          data-testid="recruit-hero-video"
        >
          <source src="https://assets.mixkit.co/videos/11/11-720.mp4" type="video/mp4" />
          <source src="https://assets.mixkit.co/videos/4067/4067-720.mp4" type="video/mp4" />
        </video>
        {/* Depth: brand-black overlay + soft rose vignette to keep text crisp on any frame */}
        <div className="absolute inset-0 bg-[#050303]/60" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#050303]/80 via-[#050303]/40 to-[#bf858e]/30" />
        <div className="relative max-w-4xl text-white">
          <div className="overline mb-4 text-[#e1b7c1]">Now Recruiting · Metro Atlanta</div>
          <h1 className="font-heading text-5xl lg:text-7xl leading-[1.05] tracking-tight">You&apos;re not just an agent. You&apos;re the CORE of what we do.</h1>
          <p className="mt-6 text-lg text-white/85 max-w-2xl">At CORE Realty and Associates, that means real support, real coaching, and real growth — built around you.</p>
          <a href="#apply" className="mt-10 inline-flex items-center gap-2 bg-[#e1b7c1] text-[#050303] pill-btn hover:bg-white" data-testid="recruit-cta">
            Apply to CORE Realty and Associates <ArrowRight size={16} />
          </a>
        </div>
      </section>

      <section className="px-8 lg:px-16 py-20 max-w-7xl" data-testid="recruit-pillars">
        <div className="overline">Why CORE Realty and Associates</div>
        <h2 className="font-heading text-4xl lg:text-5xl mt-2 mb-3 max-w-3xl">A brokerage that keeps you at the center.</h2>
        <p className="text-sm text-[#050303]/70 mb-12 max-w-2xl">
          Three promises we make to every agent who signs with us — before you ever look at a split.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={p.id}
                className={`core-card p-7 ${i === 1 ? "bg-gradient-to-br from-white to-[#e1b7c1]/30" : ""}`}
                data-testid={`pillar-${p.id}`}
              >
                <div className="w-11 h-11 rounded-full bg-[#e1b7c1]/40 text-[#bf858e] flex items-center justify-center">
                  <Icon size={18} strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-2xl mt-5">{p.title}</h3>
                <ul className="mt-5 space-y-2">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-2 text-sm">
                      <Check size={14} strokeWidth={2} className="mt-0.5 text-[#bf858e] shrink-0" /> {pt}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        <div className="mt-10 text-sm text-[#050303]/70">
          <a href="mailto:info@corerealtyandassociates.com?subject=CORE%20Realty%20and%20Associates%20—%20More%20info"
             className="text-[#bf858e] hover:text-[#050303] underline underline-offset-4" data-testid="contact-link">
            Contact us for more information
          </a>
          , or{" "}
          <a href="#apply" className="text-[#bf858e] hover:text-[#050303] underline underline-offset-4" data-testid="apply-here-link">
            apply here
          </a>{" "}
          for fees and splits.
        </div>
      </section>

      <section id="apply" className="px-8 lg:px-16 py-20 bg-[#050303] text-white">
        <Logo variant="black" height={56} className="mb-10 bg-white p-3 rounded-lg inline-block" />
        <div className="max-w-2xl">
          <div className="overline text-white/70">Take the next step</div>
          <h2 className="font-heading text-5xl mt-2 mb-8">Apply to CORE Realty and Associates.</h2>
          {sent ? (
            <div className="core-card p-7 bg-white text-[#050303]" data-testid="recruit-success">
              <h3 className="font-heading text-2xl">Thank you.</h3>
              <p className="text-sm mt-2 text-[#050303]/70">Our broker will reach out within 48 hours.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="recruit-form">
              <input required placeholder="Full name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[#e1b7c1]" data-testid="apply-name" />
              <input required type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[#e1b7c1]" data-testid="apply-email" />
              <input placeholder="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[#e1b7c1]" data-testid="apply-phone" />
              <input placeholder="Years licensed (optional)" value={form.years} onChange={e => setForm({ ...form, years: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[#e1b7c1]" data-testid="apply-years" />
              <textarea placeholder="Why CORE Realty and Associates?" rows={4} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:border-[#e1b7c1]" data-testid="apply-message" />
              <button type="submit" className="bg-[#e1b7c1] text-[#050303] pill-btn hover:bg-white" data-testid="apply-submit">Submit Application</button>
            </form>
          )}
        </div>
      </section>

      <footer className="px-8 py-8 border-t border-[#d1cece]" data-testid="recruit-footer">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Logo variant="black" height={32} />
            <span
              className="text-[10px] font-semibold tracking-[0.18em] uppercase text-[#bf858e]"
              data-testid="brand-motto"
            >
              Real Estate Integrity at its CORE
            </span>
          </div>
          <div className="text-xs text-[#050303]/50">
            © {new Date().getFullYear()} CORE Realty and Associates LLC · Snellville, GA
          </div>
        </div>
      </footer>
    </div>
  );
}
